import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { extractErrorMessage } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import {
  najdiDielec,
  predchadzajuceHodnoty,
} from "@/lib/undo/dielecUndo";
import type {
  DataResponse,
  Dielec,
  DielecUpdate,
  ZakazkaDetail,
} from "@/api/schemas";

/**
 * Hromadná úprava a mazanie dielcov.
 *
 * Backend **nemá** bulk endpoint a zámerne ho nedostal: `PATCH /dielce/{id}`
 * má bohaté vedľajšie účinky (`resolve_auto_edges`, `apply_nazov_preset`,
 * XOR slot/direct, validácia hrúbky vs. materiál) a vlastná bulk cesta by
 * ich musela duplikovať — časom by sa rozišli. Voláme teda existujúci PATCH
 * sekvenčne; ten istý vzor už v produkcii používa `VymenaDialog`.
 *
 * Rozdiel k lepšiemu: `useUpdateDielec` invaliduje `zakazky.detail`
 * v `onSettled`, takže hromadná zmena 20 dielcov znamenala 20 refetchov
 * veľkého payloadu. Tu sa invaliduje **raz na konci**.
 *
 * Chyba na jednom dielci nezastaví zvyšok — vráti sa v `chyby`, aby user
 * vedel, čo neprešlo (typicky hrúbka dielca vs. materiál slotu).
 */

/** Upozornenia na hrany, ktoré úprava jedného dielca vrátila (KOLO 135). */
export interface UpozorneniaDielca {
  id: string;
  kod: string | null;
  upozornenia: NonNullable<Dielec["upozornenia_hran"]>;
}

export interface BulkVysledok {
  /** Počet dielcov, ktoré sa reálne zmenili. */
  hotovo: number;
  /** Dielce, ktoré neprešli — `kod` (alebo id) a dôvod. */
  chyby: { id: string; sprava: string }[];
  /**
   * Upozornenia na hrany z odpovedí PATCH — len dielce, ktoré nejaké
   * vrátili (KOLO 169). Dovtedy sa odpoveď zahadzovala, takže hromadná
   * zmena slotu či hrán vymenila hrany potichu. Toast robí volajúci
   * **raz za celú akciu** (`ohlasUpozorneniaHranHromadne`) — hromadná
   * úprava volá túto mutáciu aj per dielec a toast za každé volanie by
   * zaplavil obrazovku. Mazanie upozornenia nemá.
   */
  upozornenia?: UpozorneniaDielca[];
}

export interface BulkUpdateVstup {
  ids: string[];
  data: DielecUpdate;
}

/** Sekvenčný PATCH nad viacerými dielcami, jedna invalidácia na konci. */
export function useBulkUpdateDielce(
  zakazkaId: string,
): UseMutationResult<BulkVysledok, Error, BulkUpdateVstup> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async ({ ids, data }) => {
      const chyby: BulkVysledok["chyby"] = [];
      const hotoveIds: string[] = [];
      const upozornenia: UpozorneniaDielca[] = [];
      for (const id of ids) {
        try {
          const { data: odpoved } = await api.patch<DataResponse<Dielec>>(
            `/dielce/${id}`,
            data,
          );
          hotoveIds.push(id);
          const u = odpoved?.data?.upozornenia_hran ?? [];
          if (u.length > 0) {
            upozornenia.push({ id, kod: odpoved.data.kod ?? null, upozornenia: u });
          }
        } catch (err) {
          chyby.push({ id, sprava: extractErrorMessage(err) });
        }
      }
      return { hotovo: hotoveIds.length, chyby, upozornenia, hotoveIds };
    },
    onMutate: ({ ids, data }) => {
      // Predchádzajúce hodnoty **per dielec** — vybrané dielce môžu mať
      // rôzne sloty aj hrany, takže jedna spoločná hodnota by ich pri
      // vrátení zrovnala na to isté.
      const detail = qc.getQueryData<DataResponse<ZakazkaDetail>>(
        queryKeys.zakazky.detail(zakazkaId),
      );
      const puvodne = new Map<string, DielecUpdate>();
      for (const id of ids) {
        puvodne.set(
          id,
          predchadzajuceHodnoty(najdiDielec(detail?.data, id), data),
        );
      }
      return { puvodne };
    },
    onSuccess: (vysledok, _vstup, context) => {
      const hotoveIds = (vysledok as BulkVysledok & { hotoveIds?: string[] })
        .hotoveIds;
      if (!hotoveIds?.length || !context?.puvodne) return;
      // Jeden krok pre N dielcov — hromadná zmena bola jedno kliknutie,
      // takže jej vrátenie má byť jedno Ctrl+Z, nie N.
      undo?.pridaj({
        popis: `hromadnú zmenu ${hotoveIds.length} dielcov`,
        vrat: async () => {
          for (const id of hotoveIds) {
            const naspat = context.puvodne.get(id);
            if (naspat && Object.keys(naspat).length > 0) {
              await api.patch(`/dielce/${id}`, naspat);
            }
          }
          invaliduj(qc, zakazkaId);
        },
      });
    },
    onSettled: () => invaliduj(qc, zakazkaId),
  });
}

/**
 * Sekvenčný DELETE (soft — backend nastaví `deleted_at`).
 * Per-dielec `data` sa nelíši, takže vstupom je len zoznam id.
 */
export function useBulkDeleteDielce(
  zakazkaId: string,
): UseMutationResult<BulkVysledok, Error, string[]> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const chyby: BulkVysledok["chyby"] = [];
      const hotoveIds: string[] = [];
      for (const id of ids) {
        try {
          await api.delete(`/dielce/${id}`);
          hotoveIds.push(id);
        } catch (err) {
          chyby.push({ id, sprava: extractErrorMessage(err) });
        }
      }
      return { hotovo: hotoveIds.length, chyby, hotoveIds };
    },
    onSuccess: (vysledok) => {
      const hotoveIds = (vysledok as BulkVysledok & { hotoveIds?: string[] })
        .hotoveIds;
      if (!hotoveIds?.length) return;
      undo?.pridaj({
        popis: `zmazanie ${hotoveIds.length} dielcov`,
        vrat: async () => {
          for (const id of hotoveIds) {
            await api.post(`/dielce/${id}/obnovit`);
          }
          invaliduj(qc, zakazkaId);
        },
      });
    },
    onSettled: () => invaliduj(qc, zakazkaId),
  });
}

/** Rovnaká sada ako v `useUpdateDielec` — hrany a kovania menia sumáre. */
function invaliduj(
  qc: ReturnType<typeof useQueryClient>,
  zakazkaId: string,
): void {
  qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
  qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() });
  qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() });
}
